import React, { useState, useEffect } from 'react';
import './InPageNotificationToast.css';

let notificationQueue = [];
let showNotificationCallback = null;

// API global para mostrar notificaciones desde cualquier componente
if (typeof window !== 'undefined') {
  window.showInPageNotification = (title, message, type = 'info', duration = 8000) => {
    try {
      const notification = {
        id: Date.now() + Math.random(),
        title: String(title || 'Notificación'),
        message: String(message || ''),
        type: String(type || 'info'),
        duration: Number(duration) || 8000,
        timestamp: new Date()
      };
      
      notificationQueue.push(notification);
      if (showNotificationCallback) {
        showNotificationCallback();
      }
    } catch (error) {
      console.error('[InPageNotificationToast] Error creando notificación:', error);
    }
  };
}

const getNotificationIcon = (type) => {
  switch (type) {
    case 'success': return 'bi-check-circle-fill';
    case 'warning': return 'bi-exclamation-triangle-fill';
    case 'error': return 'bi-x-circle-fill';
    case 'pending': return 'bi-clock-fill';
    case 'assigned': return 'bi-person-fill';
    case 'urgent': return 'bi-lightning-fill';
    default: return 'bi-info-circle-fill';
  }
};

const getNotificationColor = (type) => {
  switch (type) {
    case 'success': return '#10b981';
    case 'warning': return '#f59e0b';
    case 'error': return '#ef4444';
    case 'pending': return '#ca8a04'; // Ámbar sobrio (atención sin aspecto de error)
    case 'assigned': return '#3b82f6';
    case 'urgent': return '#dc2626';
    default: return '#6366f1';
  }
};

export default function InPageNotificationToast() {
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    try {
      showNotificationCallback = () => {
        try {
          if (notificationQueue.length > 0) {
            const newNotification = notificationQueue.shift();
            if (newNotification) {
              setNotifications(prev => [...prev, newNotification]);
              
              // Auto-remove después del duration
              setTimeout(() => {
                setNotifications(prev => prev.filter(n => n.id !== newNotification.id));
              }, newNotification.duration || 8000);
            }
          }
        } catch (callbackError) {
          console.error('[InPageNotificationToast] Error en showNotificationCallback:', callbackError);
        }
      };

      // Procesar cola inicial
      if (notificationQueue.length > 0) {
        showNotificationCallback();
      }

      return () => {
        showNotificationCallback = null;
      };
    } catch (error) {
      console.error('[InPageNotificationToast] Error en useEffect:', error);
    }
  }, []);

  const removeNotification = (id) => {
    try {
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('[InPageNotificationToast] Error removiendo notificación:', error);
    }
  };

  return (
    <div className="in-page-notifications-container">
      {notifications.map((notification) => {
        const isPendingStyle = notification.type === 'pending';
        return (
        <div
          key={notification.id}
          role="alert"
          className={`in-page-notification in-page-notification-${notification.type}${isPendingStyle ? ' in-page-notification--elevated' : ''}`}
          style={{ '--notification-color': getNotificationColor(notification.type) }}
        >
          {isPendingStyle ? (
            <>
              <div className="in-page-notification-pending-topbar" aria-hidden="true" />
              <div className="in-page-notification-pending-body">
                <div className="in-page-notification-header in-page-notification-header--pending">
                  <div className="in-page-notification-icon-wrap">
                    <div className="in-page-notification-icon in-page-notification-icon--lg">
                      <i className={`bi ${getNotificationIcon(notification.type)}`} aria-hidden="true"></i>
                    </div>
                  </div>
                  <div className="in-page-notification-text-block">
                    <span className="in-page-notification-kicker">Recordatorio</span>
                    <div className="in-page-notification-title in-page-notification-title--pending">
                      {notification.title}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="in-page-notification-close in-page-notification-close--pending"
                    onClick={() => removeNotification(notification.id)}
                    aria-label="Cerrar notificación"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </div>
                <p className="in-page-notification-message in-page-notification-message--pending">
                  {notification.message}
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="in-page-notification-header">
                <div className="in-page-notification-icon">
                  <i className={`bi ${getNotificationIcon(notification.type)}`}></i>
                </div>
                <div className="in-page-notification-title">
                  {notification.title}
                </div>
                <button
                  type="button"
                  className="in-page-notification-close"
                  onClick={() => removeNotification(notification.id)}
                  aria-label="Cerrar notificación"
                >
                  <i className="bi bi-x"></i>
                </button>
              </div>
              <div className="in-page-notification-message">
                {notification.message}
              </div>
            </>
          )}
          <div className="in-page-notification-progress">
            <div
              className="in-page-notification-progress-bar"
              style={{
                animationDuration: `${notification.duration}ms`,
                backgroundColor: getNotificationColor(notification.type),
              }}
            />
          </div>
        </div>
        );
      })}
    </div>
  );
}